import React, { useEffect, useRef } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { fmtClock, previewEnd, StagedPost } from './patreonStagedRelease';

/**
 * Plays one staged file so it can be checked before Release. For a post with
 * a preview window, "Play preview" plays exactly what non-members will hear
 * or see and stops at the end of it.
 */
interface Props {
  kind: 'audio' | 'video';
  src: string;
  preview?: StagedPost['preview'];
}

const StagedMediaPlayer: React.FC<Props> = ({ kind, src, preview }) => {
  const ref = useRef<HTMLVideoElement & HTMLAudioElement>(null);
  const stopAt = useRef<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onTime = () => {
      if (stopAt.current != null && el.currentTime >= stopAt.current) {
        el.pause();
        stopAt.current = null;
      }
    };
    el.addEventListener('timeupdate', onTime);
    return () => el.removeEventListener('timeupdate', onTime);
  }, [src]);

  const seek = (t: number, until: number | null = null) => {
    const el = ref.current;
    if (!el) return;
    stopAt.current = until;
    el.currentTime = t;
    // play() returns undefined in older browsers; a refused autoplay is fine.
    el.play()?.catch(() => undefined);
  };

  const end = previewEnd(preview ?? null);
  return (
    <Box sx={{ mt: 1 }}>
      {kind === 'video' ? (
        <video ref={ref} src={src} controls preload="metadata"
          style={{ width: '100%', maxHeight: 360, background: '#000' }} />
      ) : (
        <audio ref={ref} src={src} controls preload="metadata" style={{ width: '100%' }} />
      )}
      {preview && end != null && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
          <Button size="small" variant="outlined" onClick={() => seek(preview.start_s, end)}>
            Play preview
          </Button>
          <Button size="small" onClick={() => seek(Math.max(preview.start_s, end - 10), end)}>
            Last 10s of preview
          </Button>
          <Typography variant="caption" color="text.secondary">
            {fmtClock(preview.start_s)} to {fmtClock(end)}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

export default StagedMediaPlayer;
